import { Question, Difficulty, Category } from '../types';

/**
 * Scoring system based on difficulty and time
 */
export const DIFFICULTY_POINTS = {
  easy: 10,
  medium: 20,
  hard: 30,
  expert: 50,
} as const;

export const TIME_BONUS_MULTIPLIER = 0.5; // 0.5 points per second remaining
export const STREAK_MULTIPLIER = 0.1; // 10% bonus per streak level
export const MAX_STREAK_BONUS = 2.0; // Max 200% bonus at 10 streak

/**
 * Calculate points for a correct answer
 * @param difficulty - Question difficulty
 * @param timeRemaining - Time remaining in seconds when answered
 * @param currentStreak - Current answer streak
 * @returns Points earned
 */
export const calculatePoints = (
  difficulty: Difficulty,
  timeRemaining: number,
  currentStreak: number
): number => {
  const basePoints = DIFFICULTY_POINTS[difficulty];
  const timeBonus = Math.max(0, Math.floor(timeRemaining * TIME_BONUS_MULTIPLIER));
  const streakBonus = Math.min(
    currentStreak * STREAK_MULTIPLIER,
    MAX_STREAK_BONUS
  );
  const streakMultiplier = 1 + streakBonus;

  return Math.floor((basePoints + timeBonus) * streakMultiplier);
};

/**
 * Shuffle array using Fisher-Yates algorithm
 * @param array - Array to shuffle
 * @returns New shuffled array
 */
export const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Filter questions by category
 * @param questions - All questions
 * @param category - Category to filter by (or 'all')
 * @returns Filtered questions
 */
export const filterQuestionsByCategory = (
  questions: Question[],
  category: Category | 'all'
): Question[] => {
  if (category === 'all') {
    return questions;
  }
  return questions.filter((q) => q.category === category);
};

/**
 * Filter questions by difficulty
 * @param questions - All questions
 * @param difficulty - Difficulty to filter by (or 'mixed')
 * @returns Filtered questions
 */
export const filterQuestionsByDifficulty = (
  questions: Question[],
  difficulty: Difficulty | 'mixed'
): Question[] => {
  if (difficulty === 'mixed') {
    return questions;
  }
  return questions.filter((q) => q.difficulty === difficulty);
};

/**
 * Select random questions from a pool
 * @param questions - Question pool
 * @param count - Number of questions to select
 * @returns Random questions
 */
export const selectRandomQuestions = (
  questions: Question[],
  count: number
): Question[] => {
  const shuffled = shuffleArray(questions);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

/**
 * Get questions for a game session
 * @param allQuestions - All available questions
 * @param category - Category filter
 * @param difficulty - Difficulty filter
 * @param count - Number of questions needed
 * @returns Selected and randomized questions
 */
export const getGameQuestions = (
  allQuestions: Question[],
  category: Category | 'all' = 'all',
  difficulty: Difficulty | 'mixed' = 'mixed',
  count: number = 10
): Question[] => {
  let filtered = allQuestions;

  // Apply category filter
  filtered = filterQuestionsByCategory(filtered, category);

  // Apply difficulty filter
  filtered = filterQuestionsByDifficulty(filtered, difficulty);

  // Select random questions
  return selectRandomQuestions(filtered, count);
};

/**
 * Calculate game accuracy percentage
 * @param correctAnswers - Number of correct answers
 * @param totalQuestions - Total number of questions
 * @returns Accuracy percentage (0-100)
 */
export const calculateAccuracy = (
  correctAnswers: number,
  totalQuestions: number
): number => {
  if (totalQuestions === 0) return 0;
  return Math.round((correctAnswers / totalQuestions) * 100);
};

/**
 * Calculate total time spent on questions
 * @param timeSpentArray - Array of time spent per question in milliseconds
 * @returns Total time in milliseconds
 */
export const calculateTotalTime = (timeSpentArray: number[]): number => {
  return timeSpentArray.reduce((sum, time) => sum + time, 0);
};
